import {
  Card,
  CardContent
} from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../ui/tooltip';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../lib/utils';

// icons
import { Clock, Shield } from 'iconsax-react';

const UserRoleDropZone = ({ 
  user, 
  isSelected, 
  onSelect, 
  onViewHistory, 
  roles 
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `user-${user._id}`,
  });

  const getCurrentRole = () => {
    return roles.find(role => role.name === user.role);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none';
      case 'INACTIVE':
        return 'bg-rose-100 text-rose-700 hover:bg-rose-100 border-none';
      case 'SUSPENDED':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-none';
      default:
        return 'bg-slate-100 text-slate-700 hover:bg-slate-100 border-none';
    }
  };

  const getUserInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const currentRole = getCurrentRole();

  return (
    <Card
      ref={setNodeRef}
      onClick={onSelect}
      className={cn(
        "relative overflow-hidden transition-all duration-200 cursor-pointer border-2 p-0",
        isOver 
          ? "border-primary border-solid bg-primary/5" 
          : isSelected 
            ? "border-purple-500 border-solid bg-purple-50/30" 
            : "border-slate-200 border-dashed hover:border-primary hover:bg-slate-50/50"
      )}
    >
      <CardContent className="p-4">
        {/* Top Header Row */}
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-semibold shrink-0">
              {getUserInitials(user.name)}
            </div>
            <div className="overflow-hidden">
              <h4 className="text-sm font-semibold text-foreground truncate" title={user.name}>
                {user.name}
              </h4>
              <p className="text-xs text-muted-foreground truncate" title={user.email}>
                {user.email}
              </p>
            </div>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shrink-0 mt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
            />
          </div>
        </div>

        {/* Mid Row with Badges */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <Badge 
            variant="outline"
            className={cn(
              "flex items-center gap-1 font-medium text-[11px] px-2 py-0.5 border-slate-200",
              currentRole?.isSystem ? "text-primary border-primary/20 bg-primary/5" : "text-muted-foreground"
            )}
          >
            <Shield size={12} className="shrink-0" />
            {currentRole?.displayName || user.role}
          </Badge>
          <Badge 
            className={cn("font-medium text-[11px] px-2 py-0.5 border-none", getStatusBadgeClass(user.status))}
          >
            {user.status}
          </Badge>
        </div>

        {/* Bottom Row */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1 border-t border-slate-100">
          <span>
            Last login: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewHistory();
                  }}
                  className="p-1 rounded hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Clock size={15} />
                </button>
              </TooltipTrigger>
              <TooltipContent>View Role History</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Drop Overlay */}
        {isOver && (
          <div className="absolute inset-1.5 flex items-center justify-center bg-primary/95 border-2 border-dashed border-white rounded-md z-10">
            <span className="text-sm font-bold text-white tracking-wide shadow-sm">
              Drop Role Here
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserRoleDropZone;